import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Add as AddIcon, Download as DownloadIcon } from "@mui/icons-material";

import { ottoItemsApi } from "../../../../../services/api/multi/otto";

const OttoCategoryProvisionTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // XLSX state
  const [xlsxStatus, setXlsxStatus] = useState({
    exists: false,
    filename: null,
    fileSize: null,
    lastModified: null,
    loading: false,
  });

  // Helper function for error messages
  const getErrorMessage = (error) => {
    if (error.response) {
      return (
        error.response.data?.detail ||
        error.response.data?.message ||
        "Server error occurred"
      );
    }
    return error.message || "An unexpected error occurred";
  };

  // XLSX Management functions
  const checkXlsxStatus = async (forceRefresh = false) => {
    try {
      setXlsxStatus((prev) => ({ ...prev, loading: true }));

      const CACHE_KEY = "repricer_xlsx_status_cache";
      const cached = localStorage.getItem(CACHE_KEY);
      const CACHE_DURATION = 5 * 60 * 1000;

      if (!forceRefresh && cached && typeof forceRefresh !== "object") {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setXlsxStatus({
            exists: data.exists,
            filename: data.filename,
            fileSize: data.size,
            lastModified: data.last_modified,
            loading: false,
          });
          return;
        }
      }

      const response = await ottoItemsApi.checkXlsxExists();

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response.data,
          timestamp: Date.now(),
        }),
      );

      setXlsxStatus({
        exists: response.data.exists,
        filename: response.data.filename,
        fileSize: response.data.size,
        lastModified: response.data.last_modified,
        loading: false,
      });
    } catch (error) {
      console.error("Error checking XLSX status:", error);
      setXlsxStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleXlsxUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setXlsxStatus((prev) => ({ ...prev, loading: true }));
      setError(null);

      await ottoItemsApi.uploadXlsx(file);
      setSuccess("XLSX file uploaded successfully!");

      // update validation
      await checkSystemStatus(true);
      await checkXlsxStatus(true);
    } catch (error) {
      console.error("Error uploading XLSX:", error);
      setError(getErrorMessage(error));
      setXlsxStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleXlsxDownload = async () => {
    try {
      const response = await ottoItemsApi.downloadXlsx();

      // Create a blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        xlsxStatus.filename || "repricer_data.xlsx",
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading XLSX:", error);
      setError("Failed to download XLSX: " + getErrorMessage(error));
    }
  };

  // Load XLSX status on component mount
  useEffect(() => {
    checkXlsxStatus();
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Configuration & Status */}
        <Grid item xs={12} md={6}>
          {/* XLSX Management Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Category Provision (XLSX)
              </Typography>

              <Box mb={2}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Upload an XLSX file containing category provision mappings.
                  <br />
                  Column B should contain categories (comma-separated for
                  multiple categories per row), and Column E should contain the
                  provision percentage.
                  <br />
                  This data is used to calculate pricing based on product
                  categories.
                  <br />
                  <br />
                  You can find the data here:{" "}
                  <a
                    href="https://account.otto.market/s/article/Provision-Gebuehren"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline" }}
                  >
                    https://account.otto.market/s/article/Provision-Gebuehren
                  </a>
                </Alert>

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={
                    xlsxStatus.loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AddIcon />
                    )
                  }
                  disabled={xlsxStatus.loading}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {xlsxStatus.loading ? "Uploading..." : "Upload New XLSX"}
                  <input
                    type="file"
                    hidden
                    accept=".xlsx"
                    onChange={handleXlsxUpload}
                  />
                </Button>

                {xlsxStatus.exists && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f9f9f9" }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box>
                        <Typography variant="subtitle2">
                          Current File:
                        </Typography>
                        <Typography variant="body2">
                          Filename: {xlsxStatus.filename || "repricer.xlsx"}
                        </Typography>
                        <Typography variant="body2">
                          Size:{" "}
                          {xlsxStatus.fileSize
                            ? (xlsxStatus.fileSize / 1024).toFixed(2) + " KB"
                            : "Unknown"}
                        </Typography>
                        <Typography variant="body2">
                          Last Modified:{" "}
                          {xlsxStatus.lastModified
                            ? new Date(xlsxStatus.lastModified).toLocaleString()
                            : "Unknown"}
                        </Typography>
                      </Box>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleXlsxDownload}
                        title="Download current file"
                      >
                        Download
                      </Button>
                    </Box>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OttoCategoryProvisionTab;
